// frontend/components/PayWithWaveButton.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import API_URL from '../api/baseURL';
import apiFetch from '../api/apiFetch';
import { trackPixel } from '../utils/metaPixel';

// Accepts workshopId, supplierId, and invoiceId as props
export default function PayWithWaveButton({
  vehicleId,
  amount,
  currency = "XOF",
  type = "downpayment",
  workshopId,           // Workshop user ID
  supplierId,           // Supplier user ID
  invoiceId,            // Invoice ID (optional, for invoice payments)
  onSuccess
}) {
  // --- DEBUG: Log incoming props every render ---
  console.log("PayWithWaveButton props:", { workshopId, supplierId, invoiceId });

  const { token, user } = useAuth();
  const [session, setSession] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [polling, setPolling] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  // Poll payment status (after Wave redirect) — uses /check-status to verify
  // with Wave API.
  //
  // This used to poll every 4 seconds with a raw fetch(), never check res.ok,
  // and stop only when a payment came back 'paid'. So any failure — an expired
  // session above all — left it hammering the API every four seconds for as
  // long as the tab stayed open. It filled the server log with one
  // "Token verification failed" per poll, and told the user nothing.
  //
  // Now: it goes through apiFetch (which logs a dead session out), it gives up
  // after GIVE_UP_MS, and consecutive failures stop it rather than being
  // swallowed. A payment that has not completed in ten minutes is not going to
  // complete on the eleventh.
  useEffect(() => {
    if (!polling || !paymentId) return undefined;

    const POLL_MS = 4000;
    const GIVE_UP_MS = 10 * 60 * 1000;
    const MAX_CONSECUTIVE_FAILURES = 3;

    const startedAt = Date.now();
    let failures = 0;
    let stopped = false;

    const stop = () => { stopped = true; setPolling(false); clearInterval(interval); };

    const interval = setInterval(async () => {
      if (stopped) return;

      if (Date.now() - startedAt > GIVE_UP_MS) {
        stop();
        setStatus('idle');
        setError("Paiement non confirmé. Vérifiez dans Wave, puis actualisez la page.");
        return;
      }

      try {
        const data = await apiFetch(`${API_URL}/wave/check-status`, {
          method: 'POST',
          body: JSON.stringify({ paymentId }),
        });
        failures = 0;

        if (data?.status === 'paid') {
          stop();
          setStatus('paid');
          // Meta Pixel: real mobile-money payment confirmed
          trackPixel('Purchase', { value: Number(amount) || 0, currency });
          if (onSuccess) onSuccess(data);
        }
      } catch (err) {
        // apiFetch has already redirected on 401; there is nothing left to poll.
        if (err.status === 401) { stop(); return; }

        failures += 1;
        console.error('Polling failed:', err);
        if (failures >= MAX_CONSECUTIVE_FAILURES) {
          stop();
          setStatus('idle');
          setError("Impossible de vérifier le paiement. Actualisez la page.");
        }
      }
    }, POLL_MS);

    return () => { stopped = true; clearInterval(interval); };
    // eslint-disable-next-line
  }, [polling, paymentId, token, onSuccess]);

  const initiatePayment = async () => {
    setError("");
    setStatus("initiating");
    const phone = user?.details?.phone || user?.phone;
    if (!phone) {
      setError("No phone number found on your profile. Please update your profile with a valid phone number.");
      setStatus("idle");
      return;
    }

    // --- DEBUG: Log outgoing payload ---
    console.log("PayWithWaveButton payload before POST:", {
      phone,
      amount,
      currency,
      vehicleId,
      type,
      workshopId,
      supplierId,
      invoiceId
    });

    // Validation for required identifiers
    if (!workshopId && !invoiceId && !supplierId) {
      setError("This payment cannot proceed: workshopId, supplierId or invoiceId must be provided.");
      setStatus("idle");
      return;
    }

    // Build the payload dynamically
    const payload = {
      phone,
      amount,
      currency,
      vehicleId,
      type,
    };
    if (workshopId) payload.workshopId = workshopId;
    if (supplierId) payload.supplierId = supplierId; // [NEW]
    if (invoiceId) payload.invoiceId = invoiceId;

    try {
      // apiFetch throws on a non-2xx instead of handing back an error body to
      // be read as data, and logs a dead session out rather than failing here.
      const data = await apiFetch(`${API_URL}/wave/initiate`, {
        method: "POST",
        body: JSON.stringify(payload)
      });
      if (!data.success) throw new Error(data.message || "Payment failed");
      setSession(data.payment);
      setPaymentId(data.paymentId);
      setStatus("awaitingWave");
      // Optionally open the Wave payment page automatically:
      // window.open(data.payment.wave_launch_url, "_blank");
    } catch (err) {
      setError(err.message);
      setStatus("idle");
    }
  };

  const openWave = () => {
    if (session && session.wave_launch_url) {
      window.open(session.wave_launch_url, "_blank");
      setStatus("pending");
      setPolling(true);
    }
  };

  return (
    <div>
      <button
        onClick={initiatePayment}
        disabled={status === "initiating" || status === "awaitingWave" || status === "pending"}
        className="bg-blue-700 hover:bg-blue-800 text-white px-5 py-2 rounded"
      >
        {status === "initiating" ? "Starting..." : "Pay with Wave"}
      </button>
      {error && <div className="text-red-500 mt-2">{error}</div>}

      {/* Show "Open Wave" button after session starts */}
      {status === "awaitingWave" && session?.wave_launch_url && (
        <div className="mt-4">
          <button
            onClick={openWave}
            className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded"
          >
            Complete Payment in Wave
          </button>
          <div className="mt-2 text-xs text-gray-600">
            This will open the Wave payment page in a new tab.<br />
            When finished, return here. We’ll confirm your payment automatically.
          </div>
        </div>
      )}

      {status === "pending" && (
        <div className="mt-4">
          <div className="text-blue-900 bg-blue-100 rounded px-3 py-2 mb-2">
            Waiting for your Wave payment...
          </div>
          <div className="mt-2 text-xs text-gray-600">
            You can close this window after you finish payment.
          </div>
        </div>
      )}
      {status === "paid" && (
        <div className="mt-4 text-green-700 bg-green-100 rounded px-3 py-2 font-bold">
          Payment received! ✅
        </div>
      )}
    </div>
  );
}
